import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  TextField,
  Typography,
  Box,
} from "@mui/material";
import AppDialog from "components/common/AppDialog";
import { useGetBackendUserStorageFoldersQuery } from "features/adminUsers/adminUsers";
import { useSavePdfJobMutation, type PdfJobDetail } from "features/pdfTools/pdfTools";
import { pushNotification } from "features/notificationSlice";
import { useAppDispatch } from "features/store";
import { formatApiError } from "utils/formatApiError";
import { defaultSaveSubpath } from "./pdfJobUi";

interface SaveToStorageDialogProps {
  open: boolean;
  onClose: () => void;
  job: PdfJobDetail;
}

export default function SaveToStorageDialog({
  open,
  onClose,
  job,
}: SaveToStorageDialogProps) {
  const dispatch = useAppDispatch();
  const userId = job.user.id ?? 0;

  const { data: foldersData, isLoading: foldersLoading } =
    useGetBackendUserStorageFoldersQuery(userId, { skip: !open || !userId });
  const [saveJob, { isLoading: saving }] = useSavePdfJobMutation();

  const folders = useMemo(
    () => foldersData?.folders ?? [],
    [foldersData],
  );

  const [folderId, setFolderId] = useState<number | "">("");
  const [subpath, setSubpath] = useState("");
  const [overwrite, setOverwrite] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    setSubpath(defaultSaveSubpath(job.splitter_key));
    setOverwrite(false);
    setConflict(false);
    setError("");
  }, [open, job.splitter_key]);

  useEffect(() => {
    if (folders.length > 0 && folderId === "") {
      setFolderId(folders[0].id);
    }
  }, [folders, folderId]);

  const selectedFolder = folders.find((f) => f.id === folderId) ?? null;
  const destinationPreview = selectedFolder
    ? `${selectedFolder.folder_prefix.replace(/\/$/, "")}/${subpath.replace(/^\/+|\/+$/g, "")}/`
    : "";

  const canSave =
    !saving && !!selectedFolder && subpath.trim().replace(/\/+/g, "/").length > 0;

  const handleSave = async () => {
    if (!selectedFolder) return;
    setError("");
    try {
      await saveJob({
        jobId: job.job_id,
        user_folder_id: selectedFolder.id,
        dest_subpath: subpath.trim(),
        overwrite,
      }).unwrap();
      dispatch(
        pushNotification({
          message: "Saving started — files are being copied to Data Storage.",
          severity: "info",
        }),
      );
      onClose();
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      const message = formatApiError(
        (err as { data?: unknown })?.data,
        "Could not start saving. Please try again.",
      );
      if (status === 409) {
        setConflict(true);
      }
      setError(message);
    }
  };

  return (
    <AppDialog
      open={open}
      onClose={saving ? () => undefined : onClose}
      title="Save Result to Data Storage"
      maxWidth="sm"
      showCloseIcon={!saving}
      actions={
        <>
          <Button onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={!canSave}
            startIcon={
              saving ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {saving ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Typography variant="body2" color="text.secondary">
          The reviewed result will be copied into {job.user.username}&apos;s
          Data Storage. Files stay unchanged in the workspace until you discard
          the job.
        </Typography>

        {foldersLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : folders.length === 0 ? (
          <Alert severity="warning">
            This user has no Data Storage folders. Create one in User
            Management first.
          </Alert>
        ) : (
          <>
            <TextField
              select
              size="small"
              label="Destination folder *"
              value={folderId}
              onChange={(e) => setFolderId(Number(e.target.value))}
              disabled={saving}
            >
              {folders.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.display_name || f.folder_prefix}
                </MenuItem>
              ))}
            </TextField>

            <TextField
              size="small"
              label="Subfolder path *"
              value={subpath}
              onChange={(e) => {
                setSubpath(e.target.value);
                setConflict(false);
              }}
              disabled={saving}
              helperText="Where inside the folder the result will be placed."
            />

            {destinationPreview && (
              <Typography variant="caption" color="text.secondary">
                Files will be saved under: <b>{destinationPreview}</b>
              </Typography>
            )}

            {conflict && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={overwrite}
                    onChange={(e) => setOverwrite(e.target.checked)}
                    disabled={saving}
                  />
                }
                label="The destination is not empty — write into it anyway"
              />
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </>
        )}
      </Box>
    </AppDialog>
  );
}
